/**
 * AI 任务 API client
 *
 * Phase 3-1 / Plan §5.4 B1 / Design §8.2 ai_task 权限点。
 *
 * 包装 axios apiClient 调用 admin-service /ai-tasks 端点；
 * 返回类型与 admin-service rowToDto 形状对齐。
 * 所有写动作通过 client.ts 自动注入 Idempotency-Key。
 */
import { apiClient } from './client';

/** AI 任务状态枚举 */
export type AiTaskStatus = 'pending' | 'processing' | 'done' | 'failed' | 'cancelled';

/** AI 任务 DTO（与 admin-service rowToDto 1:1 对齐） */
export interface AiTaskDto {
  taskId: string;
  taskType: string;
  /**
   * work_id 在 content-pipeline ai_tasks 表为 NOT NULL（每条任务必属某 work），
   * 与 admin-service AiTaskRow.workId: string 对齐，不可 null。
   */
  workId: string;
  dialogueId: string | null;
  status: AiTaskStatus;
  /** 实际可能是 JSON 字符串，也可能是已被 MySQL JSON 列解析好的对象/数组，展示一律经 formatJsonField */
  inputRef: unknown;
  outputRef: unknown;
  errorMessage: string | null;
  retryCount: number;
  idempotencyKey: string | null;
  promptTemplateId: string | null;
  modelName: string | null;
  cancelReason: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  completedAt: string | null;
  cancelledAt: string | null;
  lastActorId: string | null;
}

/** 状态分布统计 */
export interface StatusDistributionDto {
  pending: number;
  processing: number;
  done: number;
  failed: number;
  cancelled: number;
}

/** 列表过滤参数 */
export interface ListAiTasksFilter {
  status?: AiTaskStatus;
  taskType?: string;
  workId?: string;
  dialogueId?: string;
  startAt?: string;
  endAt?: string;
  limit?: number;
  offset?: number;
}

/** GET /ai-tasks 列表响应（piggyback statusDistribution）*/
export interface ListAiTasksResponse {
  items: AiTaskDto[];
  total: number;
  limit: number;
  offset: number;
  statusDistribution: StatusDistributionDto;
}

/** POST /ai-tasks/:id/retry 响应 */
export interface RetryAiTaskResponse {
  taskId: string;
  status: 'pending';
  retry_count: number;
}

/** POST /ai-tasks/:id/cancel 响应 */
export interface CancelAiTaskResponse {
  taskId: string;
  status: 'cancelled';
  cancelled_at: number;
}

interface ServiceResponse<T> {
  code: number;
  message: string;
  data: T;
  requestId: string;
}

/**
 * GET /ai-tasks — 列表查询（分页 + 过滤）。
 *
 * 响应已 piggyback statusDistribution，30s 轮询时无需单独调 /status-distribution。
 */
export async function listAiTasks(filter: ListAiTasksFilter = {}): Promise<ListAiTasksResponse> {
  const params: Record<string, string | number> = {};
  for (const [k, v] of Object.entries(filter)) {
    if (v !== undefined && v !== null && v !== '') {
      params[k] = v as string | number;
    }
  }
  const resp = await apiClient.get<ServiceResponse<ListAiTasksResponse>>('/ai-tasks', { params });
  return resp.data.data;
}

/**
 * GET /ai-tasks/status-distribution — 单独状态分布（备用端点）。
 */
export async function getStatusDistribution(): Promise<StatusDistributionDto> {
  const resp = await apiClient.get<ServiceResponse<StatusDistributionDto>>(
    '/ai-tasks/status-distribution',
  );
  return resp.data.data;
}

/**
 * GET /ai-tasks/:id — 单条任务详情。
 */
export async function getAiTask(id: string): Promise<AiTaskDto> {
  const resp = await apiClient.get<ServiceResponse<AiTaskDto>>(
    `/ai-tasks/${encodeURIComponent(id)}`,
  );
  return resp.data.data;
}

/**
 * POST /ai-tasks/:id/retry — 重试失败任务（body 为空，幂等键自动注入）。
 */
export async function retryAiTask(id: string): Promise<RetryAiTaskResponse> {
  const resp = await apiClient.post<ServiceResponse<RetryAiTaskResponse>>(
    `/ai-tasks/${encodeURIComponent(id)}/retry`,
    {},
  );
  return resp.data.data;
}

/**
 * POST /ai-tasks/:id/cancel — 取消任务（reason 5..200 字符，前端已校验）。
 */
export async function cancelAiTask(id: string, reason: string): Promise<CancelAiTaskResponse> {
  const resp = await apiClient.post<ServiceResponse<CancelAiTaskResponse>>(
    `/ai-tasks/${encodeURIComponent(id)}/cancel`,
    { reason },
  );
  return resp.data.data;
}